import React from 'react';
import { WarningOutlined } from '@ant-design/icons';
import { FONT_SIZE, FONT_WEIGHT, LINE_HEIGHT, RADIUS, SPACING } from '../../constants/constants';
import { useThemeTokens } from '../../hooks/UseThemeTokens';
import { AppModal } from '../common/AppModal';
import { AppButton } from '../common/AppButton';

export interface ConfirmRow {
  label: string;
  value: React.ReactNode;
  /** 값을 강조할 때(새로 생성 건수 등). 기본 t.textPrimary. */
  emphasis?: 'accent' | 'muted';
}

interface Props {
  open:       boolean;
  title:      string;
  /** 모달 상단 안내 문장. */
  intro:      string;
  rows:       ConfirmRow[];
  confirmLabel: string;
  confirming?: boolean;
  onConfirm:  () => void;
  onCancel:   () => void;
}

/**
 * 신상품 등록 최종 확인 모달(2026-08-31, "상품 등록 개선안") — 확장팩 일괄/검색 개별 양쪽
 * 공통. 네이버 스마트스토어에 실제 상품이 생성되는 되돌리기 어려운 쓰기 작업이라, 무엇이
 * 몇 건 생기는지 표로 다시 보여주고 판매중지로 시작한다는 점을 경고 박스로 못박은 뒤에만
 * 진행한다. AppModal 위에 얹는 합성 패턴(JobModal·DropReviewModal과 같음, DESIGN_SYSTEM.md §5-4).
 */
export const RegisterConfirmModal: React.FC<Props> = ({
  open, title, intro, rows, confirmLabel, confirming, onConfirm, onCancel,
}) => {
  const t = useThemeTokens();

  const valueColor = (e?: ConfirmRow['emphasis']) =>
    e === 'accent' ? t.accent : e === 'muted' ? t.textMuted : t.textPrimary;

  return (
    <AppModal
      open={open}
      title={title}
      onCancel={onCancel}
      footer={null}
      width={520}
    >
      <p style={{
        fontSize: FONT_SIZE.sm, color: t.textSecondary, lineHeight: LINE_HEIGHT.relaxed,
        marginTop: 0, marginBottom: SPACING.md,
      }}>
        {intro}
      </p>

      <div style={{ border: `1px solid ${t.border}`, borderRadius: RADIUS.lg, overflow: 'hidden' }}>
        {rows.map((r, i) => (
          <div
            key={r.label}
            style={{
              display: 'flex', alignItems: 'center', gap: SPACING.sm,
              padding: `${SPACING.xs}px ${SPACING.md}px`, fontSize: FONT_SIZE.sm,
              borderTop: i === 0 ? undefined : `1px solid ${t.borderSubtle}`,
            }}
          >
            <span style={{ color: t.textSecondary, flex: 'none', width: 96 }}>{r.label}</span>
            <span style={{
              color: valueColor(r.emphasis),
              fontWeight: r.emphasis === 'accent' ? FONT_WEIGHT.bold : FONT_WEIGHT.regular,
            }}>
              {r.value}
            </span>
          </div>
        ))}
      </div>

      <div style={{
        marginTop: SPACING.md,
        border: `1px solid ${t.lineWarning}`, background: t.tintWarning, borderRadius: RADIUS.lg,
        padding: `${SPACING.sm}px ${SPACING.md}px`,
        fontSize: FONT_SIZE.xs, color: t.warning, lineHeight: LINE_HEIGHT.relaxed,
      }}>
        <WarningOutlined style={{ marginRight: SPACING.xxs }} />
        판매중지 상태로 생성되므로 자리표시 가격이 고객에게 노출되지 않습니다. 가격 조회
        페이지에서 시세를 반영한 뒤 판매를 시작하세요.
      </div>

      <div style={{ marginTop: SPACING.lg, display: 'flex', justifyContent: 'flex-end', gap: SPACING.xs }}>
        <AppButton variant="default" onClick={onCancel}>취소</AppButton>
        <AppButton variant="primary" loading={confirming} onClick={onConfirm}>{confirmLabel}</AppButton>
      </div>
    </AppModal>
  );
};
